import { useSession } from "@outschool/ui-auth";
import _ from "lodash";
import React, { useMemo } from "react";
import Helmet, { HelmetProps } from "react-helmet";

import { useDefaultPageTitle } from "../hooks/PageMetaDataHooks";

type MetaTag = HelmetProps["meta"][number];

/**
 * Add these header tags for all users
 */
const HEADER_TAG_DEFAULTS: MetaTag[] = [
  { property: "og:type", content: "website" }
];

/**
 * Add these header tags when the user has made at least one purchase
 */
const HEADER_TAGS_EXISTING_BUYERS: MetaTag[] = [
  {
    name: "apple-itunes-app",
    content:
      "app-id=1594191740, affilate-data=pt=123314890&ct=product-smart-app-banner"
  }
];

function addDefaultsToMeta(meta: MetaTag[], allDefaultMetaTags: MetaTag[]) {
  const newMeta = meta.slice();
  for (const defaultMeta of allDefaultMetaTags) {
    if (
      !_.some(
        meta,
        ({ property, name }) =>
          (property && property === defaultMeta.property) ||
          (name && name === defaultMeta.name)
      )
    ) {
      newMeta.unshift(defaultMeta);
    }
  }
  return newMeta;
}

export default function HeaderTags({
  title,
  meta = [],
  canonical,
  ...otherProps
}: HelmetProps & { canonical?: string }) {
  const { currentUser } = useSession();
  const link = canonical
    ? [{ rel: "canonical", href: canonical }, ...(otherProps.link || [])]
    : otherProps.link;

  const metaWithDefaults = useMemo(
    () =>
      addDefaultsToMeta(
        meta,
        Boolean(currentUser?.firstPaidEnrollment)
          ? HEADER_TAG_DEFAULTS.concat(HEADER_TAGS_EXISTING_BUYERS)
          : HEADER_TAG_DEFAULTS
      ),
    [meta, currentUser?.firstPaidEnrollment]
  );

  const defaultTitle = useDefaultPageTitle();

  return (
    <Helmet
      defaultTitle={defaultTitle}
      titleTemplate="%s | Outschool"
      title={title}
      meta={metaWithDefaults}
      {...otherProps}
      link={link}
    />
  );
}
